import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../../services/shared/auth.service';
import { ToasterService } from '../../shared/toaster/toaster.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { SocketService } from '../../services/socket.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [FormsModule, RouterModule, NavbarComponent, SpinnerComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  kmRange = 50;
  kmInput = '';
  genderFilter = '';
  showLocationModal = false;
  showGenderModal = false;
  showSettingsModal = false;
  showNotifications = false;
  notificationCount = 0;
  notificationUsers: any[] = [];
  usernameSearch: any;
  nearbyUsers: any[] = [];
  currentPage = 1;
  totalPages = 1;
  isFetching = false;
  noMoreUsers: boolean = false;
  noUsers: boolean = false;
  token = '';
  maxDistance = 5000000000;
  distanceStep = 50000;
  currentDistance = 50000;
  searchResults: any[] = [];
  searchActive: boolean = false;
  noSearch: boolean = false;
  searchTerm: string = '';
  showDeleteConfirm: boolean = false;
  likedUsers: any[] = [];
  dislikedUsers: any[] = [];
  spinner: boolean = false;
  spinMessage: string = 'Loading...';
  notiType: string = 'like';


  constructor(private router: Router, private http: HttpClient, private authService: AuthService, private toasterService: ToasterService, private socketService: SocketService) { }

  ngOnInit(): void {
    this.socketService.onUserLiked((data) => {
      // Show notification
      this.toasterService.showToast(`${data.fromOriginalName} liked you`, 'success');

      // Check if user is already in the notification list to avoid duplicates
      const alreadyExists = this.notificationUsers.some(
        (user) => user._id === data.fromUserId
      );
      if (!alreadyExists) {
        this.notificationUsers.unshift({
          _id: data.fromUserId,
          originalName: data.fromOriginalName,
          username: data.fromUsername,
        });
        this.notificationCount = this.notificationUsers.length;
      }
    });

    this.fetchUserPreferences();
    const data = sessionStorage.getItem('nearbyUsers');
    if (data) {
      this.nearbyUsers = JSON.parse(data);
      if (this.nearbyUsers.length === 0) {
        this.noUsers = true;
      }
    } else {
      this.noUsers = true;
    }

    const token = sessionStorage.getItem('token');
    const total = sessionStorage.getItem('nearbyUsersTotalPages');
    const page = sessionStorage.getItem('nearbyUsersCurrentPage');
    const dist = sessionStorage.getItem('currentDistance');
    if (dist) this.currentDistance = Number(dist);
    if (token) this.token = token;
    if (total) this.totalPages = Number(total);
    if (page) this.currentPage = Number(page);
    const wrapper = document.querySelector('.home-wrapper');
    if (wrapper) {
      wrapper.addEventListener('scroll', this.onScroll);
    }
  }

  ngOnDestroy(): void {
    const wrapper = document.querySelector('.home-wrapper');
    if (wrapper) {
      wrapper.removeEventListener('scroll', this.onScroll);
    }
  }

  filterOutLikedAndDisliked(users: any[]): any[] {
    const likedIds = new Set(this.likedUsers.map(u => u._id));
    const dislikedIds = new Set(this.dislikedUsers.map(u => u._id));
    return users.filter(user => !likedIds.has(user._id) && !dislikedIds.has(user._id));
  }

  fetchUserPreferences(): Promise<void> {
    this.fetchNotifications();
    const token = sessionStorage.getItem('token');
    if (!token) return Promise.resolve();

    this.spinner = true;
    this.spinMessage = 'Loading nearby users...';

    return new Promise((resolve) => {
      this.authService.getUserPreferences(token).subscribe({
        next: (res: any) => {
          this.spinner = false;
          this.spinMessage = '';
          this.likedUsers = res.likedUsers || [];
          this.dislikedUsers = res.dislikedUsers || [];
          resolve();
        },
        error: (err) => {
          this.spinner = false;
          this.spinMessage = '';
          this.toasterService.showToast('Failed to fetch preferences ❌', err.error?.error || 'Error');
          resolve();
        }
      });
    });
  }

  fetchNotifications() {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    this.authService.fetchNotifications(token).subscribe((res: any) => {
      // Only show likes in the notification tab
      this.notificationUsers = (res.notifications || [])
        .filter((notif: any) => notif.type === this.notiType) // this.notiType = 'like'
        .map((notif: any) => ({
          _id: notif.fromUserId,
          originalName: notif.fromOriginalName,
          username: notif.fromUsername,
        }));
      this.notificationCount = this.notificationUsers.length;
    });
  }

  markNotificationsRead() {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    this.authService.markNotificationsRead(token).subscribe(() => {
      this.notificationCount = 0;
      this.notificationUsers = [];
    });
    this.authService.deleteReadNotifications(token).subscribe(() => {
      this.notificationCount = 0;
      this.notificationUsers = [];
    });
  }

  closenotifications() {
    this.showNotifications = false;

    this.markNotificationsRead();

  }

  onNotificationsToggled(show: boolean) {
    this.showNotifications = show;
  }

  likeUser(user: any) {
    const token = sessionStorage.getItem('token');
    if (!token) return;

    this.authService.likeUser(user._id, token).subscribe({
      next: () => {
        // Your logic for updating UI
        this.toasterService.showToast('User liked successfully!', 'success');
        this.nearbyUsers = this.nearbyUsers.filter(u => u._id !== user._id);
      },
      error: (err: any) => {
        // Handle specific error messages
        if (err.error?.error === 'You already liked this user' || err.error === 'You already liked this user') {
          this.toasterService.showToast('You already liked this user!', 'info');
        } else {
          this.toasterService.showToast('Failed to like user ❌', err.error?.error || err.error || 'Error');
        }
      }
    });
  }

  dislikeUser(user: any) {
    const token = sessionStorage.getItem('token');
    if (!token) return;

    this.authService.dislikeUser(user._id, token).subscribe({
      next: () => {
        this.likedUsers = this.likedUsers.filter(u => u._id !== user._id);
        this.dislikedUsers.push(user);
        this.nearbyUsers = this.nearbyUsers.filter(u => u._id !== user._id);

      },
      error: (err: any) => {
        this.toasterService.showToast('Failed to dislike user ❌', err.error?.error || 'Error');
      }
    });
  }

  goToPreferences() {
    this.showSettingsModal = false; // Close modal
    this.router.navigate(['/user-pref']);
  }

  onScroll = (): void => {
    const wrapper = document.querySelector('.home-wrapper');
    if (!wrapper) return;
    const scrollTop = wrapper.scrollTop;
    const scrollHeight = wrapper.scrollHeight;
    const clientHeight = wrapper.clientHeight;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;
    if (
      scrolledToBottom &&
      !this.isFetching &&
      !this.noMoreUsers &&
      !this.noUsers &&
      (this.currentPage < this.totalPages || this.currentDistance < this.maxDistance)
    ) {
      this.isFetching = true;
      this.loadMoreUsers();
    }
  };

  handleSearchResults(results: any[]) {
    // If input was cleared, stop search mode
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.searchActive = false;
      this.noSearch = false;
      this.searchResults = [];
      return;
    }

    // Otherwise, update based on result
    this.searchResults = results;
    this.searchActive = true;
    this.noSearch = results.length === 0;

  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
  }

  loadMoreUsers() {
    const limit = 50;

    this.spinner = true;
    this.spinMessage = 'Loading more users...';

    this.authService
      .getNearbyUsers(this.token, this.currentDistance, this.currentPage, limit, this.genderFilter)
      .subscribe({
        next: (res: any) => {
          this.spinner = false;
          this.spinMessage = '';
          const newUsers: any[] = res.nearbyUsers || [];
          this.totalPages = res.totalPages;

          const existingIds = new Set(this.nearbyUsers.map((u: any) => u._id));
          // const uniqueNewUsers = newUsers.filter((user: any) => !existingIds.has(user._id));
          // const filteredNewUsers = this.filterOutLikedAndDisliked(uniqueNewUsers);

          const uniqueNewUsers = newUsers.filter(
            (user: any) => !existingIds.has(user._id)
          );

          const filteredNewUsers = this.filterOutLikedAndDisliked(uniqueNewUsers);

          // 🚫 Backend sent same users again → stop
          if (uniqueNewUsers.length === 0) {
            this.noMoreUsers = true;
            this.isFetching = false;
            return;
          }


          // if (filteredNewUsers.length === 0) {
          //   if (this.currentDistance + this.distanceStep > this.maxDistance) {
          //     this.noMoreUsers = true;
          //     this.isFetching = false;
          //     return;
          //   }
          //   this.currentDistance += this.distanceStep;
          //   this.currentPage = 1;
          //   const currentUniqueIds = new Set(this.nearbyUsers.map(u => u._id));
          //   if (currentUniqueIds.size === 0 || newUsers.every(u => currentUniqueIds.has(u._id))) {
          //     this.noMoreUsers = true;
          //     this.isFetching = false;
          //     return;
          //   }
          //   setTimeout(() => {
          //     this.loadMoreUsers();
          //   }, 300);
          //   return;
          // }

          if (filteredNewUsers.length === 0) {
            this.noMoreUsers = true;
            this.isFetching = false;
            return;
          }

          this.nearbyUsers = [...this.nearbyUsers, ...filteredNewUsers];

          if (this.nearbyUsers.length === 0) {
            this.noUsers = true;
            this.noMoreUsers = true;
          }

          sessionStorage.setItem('nearbyUsers', JSON.stringify(this.nearbyUsers));
          sessionStorage.setItem('nearbyUsersCurrentPage', String(this.currentPage));
          sessionStorage.setItem('nearbyUsersTotalPages', String(this.totalPages));
          sessionStorage.setItem('currentDistance', String(this.currentDistance));
          sessionStorage.setItem('genderFilter', this.genderFilter);

          this.currentPage++;
          this.isFetching = false;
        },
        error: (err) => {
          this.spinner = false;
          this.spinMessage = '';
          this.isFetching = false;
          this.toasterService.showToast('Failed to load more users ❌', err.error?.error || 'Error');
        }
      });
  }

  applyLocationFilter() {
    const km = Number(this.kmInput || this.kmRange || 1);
    const distanceInMeters = km * 1000;
    this.showLocationModal = false;
    this.nearbyUsers = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.noUsers = false;
    this.noMoreUsers = false;
    this.currentDistance = distanceInMeters;
    sessionStorage.removeItem('nearbyUsers');
    sessionStorage.removeItem('nearbyUsersCurrentPage');
    sessionStorage.removeItem('nearbyUsersTotalPages');
    sessionStorage.setItem('currentDistance', String(this.currentDistance));
    this.loadMoreUsers();
  }

  applyGenderFilter() {
    this.showGenderModal = false;
    this.nearbyUsers = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.noUsers = false;
    this.noMoreUsers = false;
    this.currentDistance = 50000;
    sessionStorage.removeItem('nearbyUsers');
    sessionStorage.removeItem('nearbyUsersCurrentPage');
    sessionStorage.removeItem('nearbyUsersTotalPages');
    sessionStorage.setItem('currentDistance', String(this.currentDistance));
    sessionStorage.setItem('genderFilter', this.genderFilter);
    this.loadMoreUsers();
  }

  deleteAccount() {
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
  }

  confirmDeleteAccount() {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const token = sessionStorage.getItem('token');

    if (!user._id || !token) {
      this.toasterService.showToast('User not logged in!', 'error');
      return;
    }

    this.authService.deleteAccount(user._id, token).subscribe({
      next: () => {
        this.toasterService.showToast('Account deleted successfully ✅', 'success');
        sessionStorage.clear();
        this.router.navigate(['/register']);
      },
      error: (err) => {
        this.toasterService.showToast('Failed to delete account ❌', err.error?.error || 'Error');
      }
    });

    this.showDeleteConfirm = false; // Close modal
  }

  clearStorage() {
    sessionStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/']);
  }

  goTochat(user: any) {
    this.router.navigate(['/chat', user._id]);
  }

  get hasUnreadNotifications(): boolean {
    return this.notificationCount > 0;
  }

}
